import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, AlertCircle, Clock, CheckCircle2 } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { ExportLog, ExportStatus, ExportType } from "@/types/exports";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";

const ExportStatusBadge = ({ status }: { status: ExportStatus }) => {
  const statusConfig = {
    [ExportStatus.PENDING]: {
      variant: "outline" as const,
      icon: Clock,
      label: "Pending",
      className: "border-yellow-200 text-yellow-700 bg-yellow-50",
    },
    [ExportStatus.PROCESSING]: {
      variant: "outline" as const,
      icon: Clock,
      label: "Processing",
      className: "border-blue-200 text-blue-700 bg-blue-50",
    },
    [ExportStatus.COMPLETED]: {
      variant: "outline" as const,
      icon: CheckCircle2,
      label: "Completed",
      className: "border-green-200 text-green-700 bg-green-50",
    },
    [ExportStatus.FAILED]: {
      variant: "outline" as const,
      icon: AlertCircle,
      label: "Failed",
      className: "border-red-200 text-red-700 bg-red-50",
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={config.className}>
      <Icon className="mr-1 h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const ExportTypeBadge = ({ type }: { type: ExportType }) => {
  const typeLabels = {
    [ExportType.NCDHHS_DATA]: "NCDHHS Data",
    [ExportType.IRB_SURVEY]: "IRB Survey",
    [ExportType.TEEN_DATA]: "Teen Data",
    [ExportType.NON_IRB_SURVEY]: "Non-IRB Survey",
    [ExportType.USERS_TO_CONTACT]: "Users to Contact",
    [ExportType.MERIDIAN_IL_DATA]: "Meridian IL",
    [ExportType.ALL_MARKETS_DATA]: "All Markets",
    [ExportType.NJ_RHT_DATA]: "NJ RHT",
  };

  return (
    <Badge variant="secondary" className="font-medium">
      {typeLabels[type]}
    </Badge>
  );
};

const DownloadButton = ({ exportLog }: { exportLog: ExportLog }) => {
  const { url: downloadUrl, isPending } = useSignedMedia(
    exportLog.file_url || undefined,
  );

  const handleDownload = () => {
    if (downloadUrl) {
      // Extract filename from S3 key or use a default
      const fileName = exportLog.file_key
        ? exportLog.file_key.split("/").pop() || "export.xlsx"
        : "export.xlsx";

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (exportLog.status !== ExportStatus.COMPLETED || !exportLog.file_url) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleDownload}
      disabled={isPending || !downloadUrl}
      className="h-8 px-2"
    >
      <Download className="h-4 w-4" />
    </Button>
  );
};

export const columns: ColumnDef<ExportLog>[] = [
  {
    accessorKey: "export_type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center">
          <ExportTypeBadge type={row.original.export_type} />
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center">
          <ExportStatusBadge status={row.original.status} />
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requested" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="text-center">{formatDate(row.original.created_at)}</div>
      );
    },
  },
  {
    accessorKey: "completed_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Completed" />
    ),
    enableSorting: true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="text-center">
          {row.original.completed_at
            ? formatDate(row.original.completed_at)
            : "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "requestedByUser",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requested By" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const user = row.original.requestedByUser;
      return (
        <div className="text-center">
          {user ? `${user.first_name} ${user.last_name}` : "-"}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center">
          <DownloadButton exportLog={row.original} />
        </div>
      );
    },
  },
];
